import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { IoCloseCircleSharp } from "react-icons/io5";
import { RiArrowDropDownLine } from "react-icons/ri";
import { FormattedMessage } from "react-intl";
import { v4 } from "uuid";
import { FieldInputTypeEnum } from "../../../enum/FormConfigEnum";
import { isNonEmptyString } from "../../../utils/ValidationHelp";
import {
  IAttachmentSetting,
  IDropdownSetting,
  IFieldLabel,
  IInputFieldSetting,
} from "../models/response/IGetFormResponse";

export const PreviewInput = ({
  fieldLabel,
  fieldSetting,
  languagePreview,
  onChange,
  value,
  isVibrate,
  optional,
}: {
  fieldLabel: IFieldLabel;
  fieldSetting: IInputFieldSetting;
  languagePreview: "en" | "zh" | "ms";
  onChange: (value: string | number) => void;
  value: string | number;
  isVibrate?: boolean;
  optional: boolean;
}) => {
  return (
    <input
      className={`w-full px-3 py-2 leading-tight text-gray-700 border-2 border-[#5f523d] rounded shadow appearance-none focus:outline-none focus:border-[#6f6149] ${
        isVibrate && !optional ? "animate-vibrate" : ""
      }`}
      id={fieldLabel.en.replaceAll(" ", "")}
      inputMode={
        fieldSetting.inputType === FieldInputTypeEnum.Number
          ? "decimal"
          : undefined
      }
      type={
        fieldSetting.inputType === FieldInputTypeEnum.Any
          ? "text"
          : fieldSetting.inputType === FieldInputTypeEnum.Date
          ? "date"
          : "number"
      }
      onChange={(e) => {
        if (fieldSetting.inputType === FieldInputTypeEnum.Number) {
          if (
            !isNaN(parseFloat(e.target.value)) ||
            !isNonEmptyString(e.target.value)
          ) {
            onChange(e.target.value);
          }
        } else {
          onChange(e.target.value);
        }
      }}
      min={
        fieldSetting.inputType === FieldInputTypeEnum.Date
          ? fieldSetting.minDate
            ? moment(fieldSetting.minDate).format("YYYY-MM-DD")
            : undefined
          : undefined
      }
      max={
        fieldSetting.inputType === FieldInputTypeEnum.Date
          ? fieldSetting.maxDate
            ? moment(fieldSetting.maxDate).format("YYYY-MM-DD")
            : undefined
          : undefined
      }
      value={value}
      placeholder={
        isNonEmptyString(fieldLabel[languagePreview])
          ? fieldLabel[languagePreview]
          : fieldLabel.en
      }
    />
  );
};

export const PreviewDropdown = ({
  fieldLabel,
  fieldSetting,
  languagePreview,
  onChange,
  value,
  isVibrate,
  optional,
}: {
  fieldLabel: IFieldLabel;
  fieldSetting: Array<IDropdownSetting>;
  languagePreview: "en" | "zh" | "ms";
  onChange: (value: string | number) => void;
  value: string | number;
  isVibrate?: boolean;
  optional: boolean;
}) => {
  return (
    <div className="relative">
      <select
        className={`${
          !isNonEmptyString(value) ? "text-gray-500" : "text-gray-700"
        } w-full px-3 py-2 leading-tight border-2 border-[#5f523d] rounded shadow appearance-none cursor-pointer focus:outline-none focus:border-[#6f6149] ${
          isVibrate && !optional ? "animate-vibrate" : ""
        }`}
        id={fieldLabel.en.replaceAll(" ", "")}
        onChange={(e) => onChange(parseInt(e.target.value))}
        value={value}
      >
        <option value="" disabled>
          {isNonEmptyString(fieldLabel[languagePreview])
            ? fieldLabel[languagePreview]
            : fieldLabel.en}
        </option>
        {fieldSetting.map((setting, index) => (
          <option key={index} value={index} className="text-gray-700">
            {isNonEmptyString(setting.label[languagePreview])
              ? setting.label[languagePreview]
              : setting.label.en}
          </option>
        ))}
      </select>
      <RiArrowDropDownLine
        className="absolute text-gray-500 top-1 right-2"
        size={30}
      />
    </div>
  );
};

export const PreviewAttachment = ({
  fieldLabel,
  fieldSetting,
  onChange,
  phone,
  file,
  isVibrate,
  optional,
}: {
  fieldLabel: IFieldLabel;
  fieldSetting: IAttachmentSetting;
  onChange: (value: Array<File>) => void;
  phone?: string;
  file: Array<File>;
  isVibrate?: boolean;
  optional: boolean;
}) => {
  const [files, setFiles] = useState<Array<File>>(file);
  const [errors, setErrors] = useState<ReactNode>("");
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      accept: { "image/*": [".jpeg", ".png", ".gif", ".jpg", ".webp"] }, // Specify file types to accept
      onDrop: (acceptedFiles) => {
        if (files.length + acceptedFiles.length > fieldSetting.maxUploadLimit) {
          // Alert or handle as necessary
          setErrors(
            <p className="text-red-900">
              <FormattedMessage
                id="maxUploadError"
                values={{ X: fieldSetting.maxUploadLimit }}
              />
            </p>
          );
          return;
        }
        setErrors("");
        const renamedFiles = acceptedFiles.map((file) => {
          const newFileName = v4();

          return new File([file], newFileName, {
            type: file.type,
            lastModified: file.lastModified,
          });
        });
        setFiles([...files, ...renamedFiles]);
      },
      noClick: files.length >= fieldSetting.maxUploadLimit,
      noKeyboard: files.length >= fieldSetting.maxUploadLimit,
    });

  const filePreviews = files.map((file, index) => (
    <div
      key={index}
      className="relative w-24 h-24 mb-4 border border-gray-200 rounded shadow-lg"
    >
      <IoCloseCircleSharp
        className="absolute top-0 right-0 text-red-900 cursor-pointer"
        size={20}
        style={{ transform: "translate(50%, -50%)" }}
        onClick={() => {
          setFiles((prev) => prev.filter((file, idx) => index !== idx));
        }}
      />
      <div className="flex items-center justify-center w-24 h-24 overflow-hidden">
        <img
          src={URL.createObjectURL(file)}
          alt={`preview ${index}`}
          className="w-full h-auto max-w-xs rounded-md"
        />
      </div>
    </div>
  ));

  useEffect(() => {
    onChange(files);
  }, [files]);

  return (
    <>
      {files.length > 0 && (
        <div className="flex flex-wrap items-center gap-4">{filePreviews}</div>
      )}
      <div
        {...getRootProps({
          className: `dropzone border-dashed border-2 border-[#5f523d] p-4 text-center cursor-pointer ${
            isVibrate && !optional ? "animate-vibrate" : ""
          }`,
        })}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-[#6e5f49]">
            <FormattedMessage id="dropFilesHere" />
          </p>
        ) : (
          <p className="text-[#6e5f49]">
            <FormattedMessage id="dragDropFilesHere" />
          </p>
        )}
        {errors}
      </div>
    </>
  );
};
